import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

interface UsePaginationParamsOptions {
  defaultPage?: number;
  defaultLimit?: number;
  pageKey?: string;
  limitKey?: string;
  totalPages?: number;
}

interface UsePaginationParamsReturn {
  page: number;
  limit: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
}

function parsePositiveInt(value: string | null, defaultValue: number): number {
  if (value === null) return defaultValue;
  const parsed = parseInt(value, 10);
  if (!Number.isInteger(parsed) || parsed <= 0) return defaultValue;
  return parsed;
}

export const usePaginationParams = (
  options?: UsePaginationParamsOptions,
): UsePaginationParamsReturn => {
  const {
    defaultPage = 1,
    defaultLimit = 20,
    pageKey = 'page',
    limitKey = 'limit',
    totalPages,
  } = options ?? {};

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const page = parsePositiveInt(searchParams.get(pageKey), defaultPage);
  const limit = parsePositiveInt(searchParams.get(limitKey), defaultLimit);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setPage = (newPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (newPage === defaultPage) {
      next.delete(pageKey);
    } else {
      next.set(pageKey, String(newPage));
    }
    navigate({ search: next.toString() }, { replace: true });
  };

  useEffect(() => {
    if (totalPages != null && totalPages > 0 && page > totalPages) {
      setPage(1);
    }
  }, [page, totalPages, setPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLimit = (newLimit: number) => {
    const next = new URLSearchParams(searchParams);
    if (newLimit === defaultLimit) {
      next.delete(limitKey);
    } else {
      next.set(limitKey, String(newLimit));
    }
    next.delete(pageKey);
    navigate({ search: next.toString() }, { replace: true });
  };

  return { page, limit, setPage, setLimit };
};
